import $j from 'jquery';
import { Ability } from './ability';
import { search } from './utility/pathfinding';
import { Hex } from './utility/hex';
import Game from './game';
import * as arrayUtils from './utility/arrayUtils';
import { Drop, DropDefinition } from './drop';
import { Point, getPointFacade } from './utility/pointfacade';
import { Effect } from './effect';
import { Player, PlayerID } from './player';
import { Damage, DamageResult } from './damage';
import { AugmentedMatrix } from './utility/matrices';
import { Trap } from './utility/trap';
import { HEX_WIDTH_PX, hashOffsetCoords, offsetNeighbors } from './utility/const';
import { CreatureType, Level, Realm, Unit, UnitName } from './data/types';
import { UnitDisplayInfo, UnitSize } from './data/units';

// To fix @ts-expect-error 2554: properly type the arguments for the trigger functions in `game.ts`

export type CreatureVitals = {
	health: number;
	regrowth: number;
	endurance: number;
	energy: number;
	meditation: number;
	initiative: number;
	offense: number;
	defense: number;
	movement: number;
};

export type CreatureMasteries = {
	pierce: number;
	slash: number;
	crush: number;
	shock: number;
	burn: number;
	frost: number;
	poison: number;
	sonic: number;
	mental: number;
};

export type Movement = 'normal' | 'flying' | 'hover';

type CreatureStats = CreatureVitals &
	CreatureMasteries & {
		moveable: boolean;
		fatigueImmunity: boolean;
		reqEnergy: number;
	};

type TracePositionOptions = Partial<{
	x: number;
	y: number;
	overlayClass: string;
	displayClass: string;
	drawOverCreatureTiles: boolean;
}>;

type QueryMoveOptions = Partial<{
	targeting: boolean;
	noPath: boolean;
	isAbility: boolean;
	ownCreatureHexShade: boolean;
	range: Hex[];
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	callback: (hex: Hex, args: any) => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	args: any;
}>;

type Status = {
	frozen: boolean;
	cryostasis: boolean;
	dizzy: boolean;
};

type CreatureRuntimeFlags = {
	hideFromQueue?: boolean;
	hideFromCreatureCount?: boolean;
	deathAnimationType?: string;
};

/**
 * Creature Class
 *
 * Creature contains all creatures properties and attacks/imgres?imgurl=https://i.pinimg.com/originals/e0/f0/53/e0f05354e9ca1ab73b860a896238d553.jpg&tbnid=FRJ2QLlrmIxDtM&vet=1&imgrefurl=https://www.pinterest.com/pin/346706871308486887/&docid=GMQJmxHxt2C2zM&w=931&h=807&hl=en-US&source=sh/x/im/4
 */
export class Creature {
	//TODO: This can be removed when it's factored out of get fatigueText
	#fatigueText = '';

	/* Attributes
	 *
	 * NOTE : attributes and variables starting with $ are jquery element
	 * and jquery function can be called directly from them.
	 *
	 * // Jquery attributes
	 * $display :		Creature representation
	 * $effects :		Effects container (inside $display)
	 *
	 * // Normal attributes
	 * x :				Integer :	Hex coordinates
	 * y :				Integer :	Hex coordinates
	 * pos :			Object :	Pos object for hex comparison {x,y}
	 *
	 * name :			String :	Creature name
	 * id :			Integer :	Creature Id incrementing for each creature starting to 1
	 * size :			Integer :	Creature size in hexes (1,2 or 3)
	 * type :			String :	Type of the creature stored in the database. Made up of `creature.realm` + `creature.level`. exception for Dark Priest "--"
	 * team :			Integer :	Owner's ID (0,1,2 or 3)
	 * player :		Player :	Player object shortcut
	 * hexagons :		Array :		Array containing the hexes where the creature is
	 *
	 * dead :			Boolean :	True if dead
	 * stats :			Object :	Object containing stats of the creature
	 * statsAlt :		Object :	Object containing the alteration value for each stat // TODO
	 * abilities :		Array :		Array containing the 4 abilities
	 * remainingMove : Integer :	Remaining moves allowed until the end of turn
	 * temp :           Boolean :   True if the creature is only temporary for preview, false otherwise
	 *
	 */

	// Engine
	game: Game;
	name: UnitName;
	id: number;
	x: number;
	y: number;
	pos: Point;
	size: UnitSize;
	type: CreatureType;
	level: Level;
	realm: Realm;
	animation: { walk_speed: number };
	display: UnitDisplayInfo;
	drop: DropDefinition;
	_movementType: Movement;
	temp: boolean;
	hexagons: Hex[];
	team: PlayerID;
	player: Player;

	// Game
	dead: boolean;
	undead: boolean;
	killer: Player;
	hasWait: boolean;
	travelDist: number;
	effects: Array<Effect>;
	dropCollection: Drop[];
	protectedFromFatigue: boolean;
	turnsActive: number;
	private _nextGameTurnActive: number;
	private _waitedTurn: number;
	private _hinderedTurn: number;
	materializationSickness: boolean;
	noActionPossible: boolean;

	// Statistics
	baseStats: CreatureStats;
	stats: CreatureStats;
	status: Status;
	health: number;
	oldHealth: number;
	endurance: number;
	energy: number;
	oldEnergy: number;
	remainingMove: number;
	abilities: Ability[];
	accumulatedTeleportRange = 0; // Used for Abolished's third ability
	// BRB state — used by Gumble's upgraded Gooey Body to defer death
	_brbActive: boolean;
	_brbState: { killer: Creature | { player: Player }; gooTrap: Trap } | null;
	/** True after BRB has fired once; prevents Gooey Body re-triggering on the follow-up death. */
	_brbSpent: boolean;

	creatureSprite: CreatureSprite;

	/**
	 * @constructor
	 * @param{Object} obj - Object containing all creature stats
	 * @param{Game} game - Game instance
	 */
	constructor(
		obj: Unit & {
			// These properties are created by the `summon` method in `player.ts`
			x: number;
			y: number;
			team: PlayerID;
			temp: boolean;
			// These are properties that might not exists on all creatures
			type?: CreatureType;
			drop?: DropDefinition;
			display?: UnitDisplayInfo;
			movementType?: Movement;
			// This depends on player._summonCreaturesWithMaterializationSickness
			materializationSickness?: boolean;
		},
		game: Game,
	) {
		// Engine
		this.game = game;
		this.name = obj.name;
		this.id = game.creatures.length;
		this.x = obj.x - 0;
		this.y = obj.y - 0;
		this.pos = {
			x: this.x,
			y: this.y,
		};
		this.size = obj.size;
		this.type = obj.type;
		this.level = obj.level;
		this.realm = obj.realm;
		this.animation = obj.animation;
		this.display = obj.display;
		this.drop = obj.drop;
		this._movementType = 'normal';
		this.temp = obj.temp;

		if (obj.movementType) {
			this._movementType = obj.movementType;
		}

		this.hexagons = [];

		// Game
		this.team = obj.team; // = playerID (0,1,2,3)
		this.player = game.players[obj.team];
		this.dead = false;
		this.killer = undefined;
		this.hasWait = false;
		this.travelDist = 0;
		this.effects = [];
		this.dropCollection = [];
		this.protectedFromFatigue = this.isDarkPriest() ? true : false;
		this.turnsActive = 0;
		this._brbActive = false;
		this._brbState = null;
		this._brbSpent = false;

		// Statistics
		this.baseStats = {
			health: obj.stats.health - 0,
			endurance: obj.stats.endurance - 0,
			regrowth: obj.stats.regrowth - 0,
			energy: obj.stats.energy - 0,
			meditation: obj.stats.meditation - 0,
			initiative: obj.stats.initiative - 0,
			offense: obj.stats.offense - 0,
			defense: obj.stats.defense - 0,
			movement: obj.stats.movement - 0,
			pierce: obj.stats.pierce - 0,
			slash: obj.stats.slash - 0,
			crush: obj.stats.crush - 0,
			shock: obj.stats.shock - 0,
			burn: obj.stats.burn - 0,
			frost: obj.stats.frost - 0,
			poison: obj.stats.poison - 0,
			sonic: obj.stats.sonic - 0,
			mental: obj.stats.mental - 0,

			/* TODO: Move boolean flags into this.status, because updateAlterations() resets
			this.stats unless they've been applied via an effect. */
			moveable: true,
			fatigueImmunity: false,
			// Extra energy required for abilities
			reqEnergy: 0,
		};

		this.stats = {
			...this.baseStats,

			/**
			 * Represents the available "pool" or maximum health of the creature.
			 * `this.health` represents the current remaining health which cannot exceed
			 * this value.
			 */
			health: this.baseStats.health,

			/**
			 * Represents the available "pool" or maximum energy of the creature.
			 * `this.energy` represents the current remaining energy which cannot exceed
			 * this value.
			 */
			energy: this.baseStats.energy,

			/**
			 * Represents the available "pool" or maximum endurance of the creature.
			 * `this.endurance` represents the current remaining endurance which cannot
			 * exceed this value. It also cannot be lower than 0.
			 */
			endurance: this.baseStats.endurance,

			/**
			 * Represents the available "pool" or maximum movement of the creature.
			 * `this.remainingMove` represents the current remaining movement which cannot
			 * exceed this value.
			 */
			movement: this.baseStats.movement,
		};

		this.status = {
			/**
			 * "Frozen" creature will miss their next turn. Frozen expires at the end
			 * of their next (missed) turn. Any damage will break the frozen status.
			 */
			frozen: false,

			/**
			 * "Cryostasis" enhances the "Frozen" status to not break on damage from any
			 * source.
			 */
			cryostasis: false,

			/**
			 * Another type of "Frozen", with a different name.
			 */
			dizzy: false,
		};

		// Current health. Maximum health is `this.stats.health`.
		this.health = obj.stats.health;
		// Current endurance. Maximum endurance is `this.stats.endurance`.
		this.endurance = obj.stats.endurance;
		// Current energy. Maximum energy is `this.stats.energy`.
		this.energy = obj.stats.energy;
		// Current movement. Maximum movement is `this.stats.movement`.
		this.remainingMove = 0; //Default value recovered each turn

		// Abilities
		this.abilities = [
			new Ability(this, 0, game),
			new Ability(this, 1, game),
			new Ability(this, 2, game),
			new Ability(this, 3, game),
		];

		this.updateHex();

		this.creatureSprite = new CreatureSprite(this);

		if (!this.temp) {
			let tempCreature: Creature | undefined = undefined;
			for (const other of game.creatures) {
				if (other && other.type === this.type && other.team === this.team && other.temp) {
					/**
					 *  NOTE:
					 * `this` is the summoned version of `other`
					 *
					 * `this` is a summoned Creature: temp == false.
					 * `other` is an "unmaterialized" Creature: temp == true.
					 *
					 * Use the "unmaterialized" creature's id so that `this` will replace
					 * `other` in `game.creatures`.
					 */
					tempCreature = other;
				}
			}
			if (tempCreature) {
				const oldId = this.id;
				this.id = tempCreature.id;
				tempCreature.destroy();
				// FX state was keyed under oldId; rekey to match the final assigned id.
				if (this.name === 'Infernal') {
					game.animations.rekeyInfernalCardboardEffect(this, oldId);
				}
			}
		}
		// Adding Himself to creature arrays and queue
		game.creatures[this.id] = this;
		if (typeof obj.materializationSickness !== 'undefined') {
			this.materializationSickness = obj.materializationSickness;
		} else {
			this.materializationSickness = this.isDarkPriest() ? false : true;
		}
		this.noActionPossible = false;

		this._nextGameTurnActive =
			!this.materializationSickness || this.isDarkPriest() ? this.game.turn : this.game.turn + 1;
		this._waitedTurn = -1;
		this._hinderedTurn = -1;
	}

	// NOTE: These fields previously existed on Creature
	// but are now part of their own class.
	// TODO: These should be factored out when possible,
	// as their use constitutes a Demeter violation.
	get grp() {
		return this.creatureSprite.grp;
	}
	get sprite() {
		return this.creatureSprite.sprite;
	}

	get legacyProjectileEmissionPoint() {
		return this.creatureSprite.legacyProjectileEmissionPoint;
	}

	/**
	 * Summon animation.
	 */
	summon(disableMaterializationSickness = false) {
		const game = this.game;

		if (disableMaterializationSickness) {
			this.materializationSickness = false;
		}

		game.updateQueueDisplay();

		game.grid.orderCreatureZ();

		// Materialization fade-in: 1 second per occupied hex
		const fadeMs = 1000 * this.size;
		game.grid.fadeOutTempCreature(undefined, fadeMs);
		this.creatureSprite.setAlpha(1, fadeMs);
		setTimeout(() => {
			if (this.dead) {
				return;
			}

			const materializedGroup = this.creatureSprite?.grp;
			if (materializedGroup && materializedGroup.alpha < 1) {
				this.creatureSprite.setAlpha(1);
			}
		}, fadeMs + 50);

		// Ghost creatures in front so the materializing unit is spotlighted
		this.hexagons.forEach((hex) => hex.ghostOverlap(this));
		setTimeout(() => {
			if (!game.grid) return;
			if (game.grid.lastXrayHex) {
				game.grid.xray(game.grid.lastXrayHex);
			} else {
				game.creatures.forEach((c) => {
					if (c instanceof Creature) c.xray(false);
				});
			}
		}, fadeMs + 600);

		// Reveal and position health indicator
		this.updateHealth();
		this.healthShow();

		// Trigger trap under
		this.hexagons.forEach((hex) => {
			hex.activateTrap(game.triggers.onStepIn, this);
		});

		// Pickup drop
		this.pickupDrop();
		if (!(this as CreatureRuntimeFlags).hideFromQueue) {
			this.hint(this.name, 'creature_name');
		}
	}

	healthHide() {
		this.creatureSprite.showHealth(false);
	}

	healthShow() {
		this.creatureSprite.showHealth(true);
	}

	/**
	 * Activate the creature by showing movement range and binding controls to this creature
	 */
	activate() {
		this.travelDist = 0;
		this.oldEnergy = this.energy;
		this.oldHealth = this.health;
		this.noActionPossible = false;

		const game = this.game;
		const stats = this.stats;
		const varReset = () => {
			this.game.onReset(this);
			// Variables reset
			this.updateAlteration();
			this.remainingMove = stats.movement;

			if (!this.materializationSickness) {
				// Fatigued creatures (endurance 0) should not regenerate.
				if (!this.isFatigued()) {
					this.heal(stats.regrowth, true);

					if (stats.meditation > 0) {
						this.recharge(stats.meditation);
					}
				} else {
					if (stats.regrowth < 0) {
						this.heal(stats.regrowth, true);
					} else {
						this.hint('♦', 'damage');
					}
				}
			} else {
				this.hint('♣', 'damage');
			}

			setTimeout(() => {
				game.UI.energyBar.animSize(this.energy / stats.energy);
				game.UI.healthBar.animSize(this.health / stats.health);
			}, 1000);

			this.endurance = stats.endurance;

			this.abilities.forEach((ability) => {
				ability.reset();
				if (!ability.upgraded && ability.usesLeftBeforeUpgrade() === 0) {
					ability.setUpgraded();
				}
			});
		};
		varReset.bind(this);

		// Frozen or dizzy effect
		if (this.isFrozen() || this.isDizzy()) {
			varReset();
			const interval = setInterval(() => {
				if (!game.turnThrottle) {
					clearInterval(interval);
					game.skipTurn({
						tooltip: this.isFrozen() ? 'Frozen' : 'Dizzy',
					});
				}
			}, 50);
			return;
		}

		// Dazzled effect — ronin units whose Dark Priest has been killed in 2vs2
		if (this.player.hasLost) {
			varReset();
			const interval = setInterval(() => {
				if (!game.turnThrottle) {
					clearInterval(interval);
					game.skipTurn({
						tooltip: 'Dazzled',
					});
				}
			}, 50);
			return;
		}

		// BRB state — Gumble's upgraded Gooey Body deferred revival
		if (this._brbState) {
			varReset();
			const brbState = this._brbState;
			const brbInterval = setInterval(() => {
				if (!game.turnThrottle) {
					clearInterval(brbInterval);

					// Trap hex is occupied — skip this turn and try again next
					if (brbState.gooTrap.hex.creature) {
						game.skipTurn({ tooltip: 'BRB' });
						return;
					}

					// Hex is free — revive!
					this._brbSpent = false; // reset so ability can fire again if Gumble dies later
					this._brbState = null;
					this.x = brbState.gooTrap.x;
					this.y = brbState.gooTrap.y;
					this.pos = { x: this.x, y: this.y };
					this.health = this.stats.health;
					this.energy = this.stats.energy;
					this.endurance = this.stats.endurance;
					this.updateHex();
					this.facePlayerDefault();

					// Revive with rise animation (inverse of melt)
					const reviveFadeMs = 1000;
					this.creatureSprite.setAngle(0, 0);
					this.creatureSprite.setHex(brbState.gooTrap.hex, 0);

					// Fade trap out while Gumble rises back in
					brbState.gooTrap.hide(reviveFadeMs);
					setTimeout(() => brbState.gooTrap.destroy(), reviveFadeMs);
					this.healthShow();
					this.updateHealth();

					game.animations.rise(this, {
						callback: () => {
							game.updateQueueDisplay();
							game.grid.updateDisplay();
						},
						overrideSpeed: reviveFadeMs,
					});

					game.log('%CreatureName' + this.id + '% rises from the goo!');
					this.hint('Back!', 'msg_effects');

					setTimeout(() => {
						game.startTimer();
						this.queryMove(null);
					}, reviveFadeMs);
				}
			}, 50);
			return;
		}

		if (!this.hasWait) {
			varReset();

			// Trigger
			// @ts-expect-error 2554
			game.onStartPhase(this);
		}

		this.materializationSickness = false;

		const interval = setInterval(() => {
			// if (!game.freezedInput) { remove for muliplayer
			clearInterval(interval);
			if (game.turn >= game.minimumTurnBeforeFleeing) {
				game.UI.btnFlee?.changeState('normal');
			}

			game.startTimer();
			this.queryMove(null);
			game.grid?.refreshHoverState();
			// }
		}, 1000);

		// Apply active-unit xray immediately; queryMove() starts after a delay and
		// can otherwise leave one-hex units visually hidden during turn handoff.
		game.grid.refreshActiveCreatureXray();
		this.xray(false);

		// Elevate health indicator above all creatures while this unit is active
		this.startBounce();
	}

	/**
	 * Deactivate the creature. Called when the creature is active, then is no longer active.
	 *
	 * @param {'wait' | 'turn-end'} reason: Why is the creature deactivated?
	 */
	deactivate(reason: 'wait' | 'turn-end') {
		const game = this.game;
		// De-elevate health indicator when this unit is no longer active
		this.resetBounce();
		this.status.frozen = false;
		this.status.cryostasis = false;
		this.status.dizzy = false;
		// Effects triggers
		if (reason === 'turn-end') {
			// Prevent queryMove() from replaying hover on the old mouse hex during
			// turn handoff, which can briefly render the active unit preview at cursor.
			game.grid.lastMouseHex = undefined;
			game.grid.suppressNextHoverRefresh = true;
			this.remainingMove = 0;
			this.queryMove(null);
			this.turnsActive += 1;
			this._nextGameTurnActive = game.turn + 1;
			// @ts-expect-error 2554
			game.onEndPhase(this);
		}
		this.hasWait = this.isDelayed;
	}

	get isInCurrentQueue() {
		return !this.dead && !this.temp && this._nextGameTurnActive <= this.game.turn;
	}

	get isInNextQueue() {
		return !this.dead;
	}

	get isDelayedInNextQueue(): null | boolean {
		if (!this.isInNextQueue) return null;
		return !this.isInCurrentQueue && this.isDelayed;
	}

	/**
	 * @deprecated Use isDelayed
	 */
	get delayed() {
		return this.isDelayed;
	}

	get isDelayed() {
		return this.isWaiting || this.isHindered;
	}

	get isWaiting() {
		return this._waitedTurn >= this.turnsActive;
	}

	get isHindered() {
		return this._hinderedTurn >= this.turnsActive;
	}

	/**
	 * @deprecated Use canWait
	 */
	get delayable() {
		return this.canWait;
	}

	/**
	 * Is waiting possible?
	 */
	get canWait() {
		const hasUnusedAbilities = this.abilities.some((a) => !a.used);
		return !this.isDelayed && this.remainingMove > 0 && hasUnusedAbilities;
	}

	/**
	 * The creature waits. It will have its turn at the end of the round.
	 * The player has decided to delay the creature until the end of the turn.
	 */
	wait(): void {
		if (this.canWait) {
			const game = this.game;

			this._waitedTurn = this.turnsActive;
			this.hint('Delayed', 'msg_effects');
			game.updateQueueDisplay();
			this.deactivate('wait');
		}
	}

	/**
	 * A creature's turn is delayed as part of an attack from another creature.
	 */
	hinder(): void {
		const game = this.game;

		this._hinderedTurn = this.turnsActive;
		this.hint('Delayed', 'msg_effects');
		game.updateQueueDisplay();
	}

	/**
	 * Launch move action query
	 */
	// TODO: type `args` in `QueryMoveOptions`
	queryMove(options?: QueryMoveOptions) {
		const game = this.game;

		if (this.dead) {
			// Creatures can die during their turns from trap effects; make sure this
			// function doesn't do anything
			return;
		}

		// Release any animation freeze that was held by a deferred ability end.
		if (game._deferredQueryMovePending > 0) {
			game._deferredQueryMovePending--;
		}
		if (game._deferredQueryMovePending === 0 && game.animationQueue.length === 0) {
			game.freezedInput = false;
			// refreshHoverState() fires at the end of queryHexes() below, after the new movement query handlers are in place.
		}

		// Once Per Damage Abilities recover
		game.creatures.forEach((creature) => {
			//For all Creature
			if (creature) {
				creature.abilities.forEach((ability) => {
					if (game.triggers.oncePerDamageChain.test(ability.getTrigger())) {
						ability.setUsed(false);
					}
				});
			}
		});

		// Clean up any abandoned temporary creatures left over from cancelled summons.
		// Only removing the last element was insufficient when other creatures were
		// added to game.creatures after an orphaned temp, leaving it stranded.
		const tempCreatures = game.creatures.filter((c) => c?.temp);
		tempCreatures.forEach((c) => c.destroy());

		let remainingMove = this.remainingMove;
		// No movement range if unmoveable
		if (!this.stats.moveable) {
			remainingMove = 0;
		}

		const defaultOptions = {
				targeting: false,
				noPath: false,
				isAbility: false,
				ownCreatureHexShade: true,
				range: game.grid.getMovementRange(this.x, this.y, remainingMove, this.size, this.id),
				callback: function (hex: Hex, args) {
					if (hex.x == args.creature.x && hex.y == args.creature.y) {
						// Prevent null movement
						game.activeCreature.queryMove();
						return;
					}

					if (game.grid.materialize_overlay) {
						const creature = game.retrieveCreatureStats(game.activeCreature.type);
						game.Phaser.add
							.tween(game.grid.materialize_overlay)
							.to(
								{
									alpha: 0,
								},
								creature.animation.walk_speed,
								Phaser.Easing.Linear.None,
							)
							.start();
					}

					game.gamelog.add({
						action: 'move',
						target: {
							x: hex.x,
							y: hex.y,
						},
					});
					if (game.multiplayer) {
						game.gameplay.moveTo({
							target: {
								x: hex.x,
								y: hex.y,
							},
						});
					}
					game.UI.btnDelay.changeState('disabled');
					args.creature.moveTo(hex, {
						animation: args.creature.movementType() === 'flying' ? 'fly' : 'walk',
						callback: function () {
							game.activeCreature.queryMove();
						},
					});
				},
			},
			// Overwrite any fields of `defaultOptions` that were provided in `options`
			o =
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				typeof ($j as any)?.extend === 'function'
					? // eslint-disable-next-line @typescript-eslint/no-explicit-any
					  ($j as any).extend(defaultOptions, options)
					: { ...defaultOptions, ...(options || {}) };

		if (!o.isAbility) {
			if (game.UI.selectedAbility != -1) {
				this.hint('Canceled', 'gamehintblack');
			}

			// In test environments jQuery might not be initialized; guard DOM ops
			if (typeof ($j as unknown as { [key: string]: unknown }) === 'function') {
				($j as unknown as (selector: string) => { removeClass: (cls: string) => void })(
					'#abilities .ability',
				).removeClass('active');
			}
			if (game.UI?.selectAbility) {
				game.UI.selectAbility(-1);
			}
			if (game.UI?.updateQueueDisplay) {
				game.UI.updateQueueDisplay();
			}
		}

		game.grid.orderCreatureZ();
		this.facePlayerDefault();
		this.updateHealth();

		// Ghost creatures that visually obstruct the active creature.
		// nextCreature() already called clearAllXray() which only fades out without
		// re-triggering ghostOverlap for the old unit, so it is safe to start the
		// new fade-in immediately.
		game.grid.refreshActiveCreatureXray();
		this.xray(false); // active creature itself must never be xrayed
		if (this.movementType() === 'flying') {
			o.range = game.grid.getFlyingRange(this.x, this.y, remainingMove, this.size, this.id);
		}

		const selectNormal = function (hex, args) {
			if (!game.grid.isRefreshingHoverState) {
				game.grid.redoLastQuery();
			}
			args.creature.tracePath(hex);
		};

		const selectFlying = function (hex, args) {
			if (!game.grid.isRefreshingHoverState) {
				game.grid.redoLastQuery();
			}
			const creature = game.retrieveCreatureStats(game.activeCreature.type);
			game.grid.previewCreature(hex, creature, game.activePlayer);
			args.creature.tracePosition({
				x: hex.x,
				y: hex.y,
				overlayClass: 'creature moveto selected player' + args.creature.team,
			});
		};
		const select = o.noPath || this.movementType() === 'flying' ? selectFlying : selectNormal;

		if (this.noActionPossible) {
			// Bots handle their own skip turn — don't show the human-facing hint/UI
			if (this.player.controller !== 'bot') {
				const buttonElement = game.UI.btnSkipTurn.$button;

				buttonElement.addClass('bounce');
				// In unit tests or headless environments, grid query APIs may be absent
				if (typeof (game.grid as unknown as { querySelf?: unknown })?.querySelf === 'function') {
					game.grid.querySelf({
						fnOnConfirm: function () {
							game.UI.btnSkipTurn.click();
						},
						// eslint-disable-next-line @typescript-eslint/no-empty-function
						fnOnCancel: function () {},
						confirmText: 'Skip turn',
					});
				}
			} else {
				// getMovementRange above calls cleanReachable() which marks all hexes
				// reachable (showing them as outline hexagons). Clean that up since bots
				// don't go through querySelf/queryHexes in this path.
				game.grid.updateDisplay();
				game.grid.forEachHex((hex) => hex.unsetReachable());
			}
		} else {
			// In unit tests or headless environments, guard against missing queryHexes()
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			if (typeof (game.grid as any)?.queryHexes === 'function') {
				game.grid.queryHexes({
					fnOnSelect: select,
					fnOnConfirm: o.callback,
					fillHexOnHover: false,
					args: {
						creature: this,
						args: o.args,
					}, // Optional args
					size: this.size,
					flipped: this.player.flipped,

					id: this.id,
					hexes: o.range,
					ownCreatureHexShade: o.ownCreatureHexShade,
					targeting: o.targeting,
				});
			}
		}
	}

	/**
	 * Preview the creature position at the given Hex
	 * @param{Hex} hex - Position
	 */
	previewPosition(hex: Hex) {
		const game = this.game;
		game.grid.allhexes.forEach((gridHex) =>
			gridHex.cleanOverlayVisualState('hover h_player' + this.team),
		);
		if (!game.grid.hexes[hex.y][hex.x].isWalkable(this.size, this.id)) {
			return; // Break if not walkable
		}

		const creat = game.retrieveCreatureStats(game.activeCreature.type);
		game.grid.previewCreature(hex.pos, creat, game.activePlayer);

		this.tracePosition({
			x: hex.x,
			y: hex.y,
			overlayClass: 'hover h_player' + this.team,
		});
	}

	startBounce() {
		this.creatureSprite.elevateHealth(true);
		this.creatureSprite.setHealthBounce(true);
	}

	resetBounce() {
		this.creatureSprite.setHealthBounce(false);
		this.creatureSprite.elevateHealth(false);
	}

	/**
	 * Clean current creature hexagons
	 */
	cleanHex() {
		this.hexagons.forEach((hex) => {
			hex.creature = undefined;
		});
		this.hexagons = [];
	}

	/**
	 * Update the current hexes containing the creature and their display
	 */
	updateHex() {
		const count = this.size;
		let i;

		for (i = 0; i < count; i++) {
			this.hexagons.push(this.game.grid.hexes[this.y][this.x - i]);
		}

		this.hexagons.forEach((hex) => {
			hex.creature = this;
		});
	}

	highlightCurrentHexesAsDashed() {
		const dashedTexture = `hex_dashed_p${this.player.id}`;
		this.hexagons.forEach((hex) => {
			if (hex.display.key !== dashedTexture) {
				hex.display.loadTexture(dashedTexture);
			}
		});
	}

	clearDashedOverlayOnHexes() {
		const playerTexture = `hex_p${this.player.id}`;
		this.hexagons.forEach((hex) => {
			if (hex.display.key !== playerTexture) {
				hex.display.loadTexture(playerTexture);
			}
		});
	}

	/**
	 * Face creature at given hex
	 * @param{Hex | Creature} facefrom - Hex to face from
	 * @param{Hex | Creature} faceto - Hex to face
	 */
	faceHex(
		faceto: Hex | Creature,
		facefrom?: Hex | Creature,
		ignoreCreaHex?: boolean,
		attackFix?: boolean,
	) {
		if (!facefrom) {
			facefrom = this.player.flipped ? this.hexagons[this.size - 1] : this.hexagons[0];
		}

		if (!facefrom) {
			this.facePlayerDefault();
			return;
		}

		if (
			ignoreCreaHex &&
			faceto instanceof Hex &&
			facefrom instanceof Hex &&
			this.hexagons.indexOf(faceto) != -1 &&
			this.hexagons.indexOf(facefrom) != -1
		) {
			this.facePlayerDefault();
			return;
		}

		if (faceto instanceof Creature) {
			if (faceto === this) {
				this.facePlayerDefault();
				return;
			}
			faceto = faceto.size < 2 ? faceto.hexagons[0] : faceto.hexagons[1];
		}

		if (faceto.x == facefrom.x && faceto.y == facefrom.y) {
			this.facePlayerDefault();
			return;
		}

		if (attackFix && this.size > 1) {
			//only works on 2hex creature targeting the adjacent row
			const flipOffset = this.player.flipped ? 1 : 0;
			if (facefrom.y % 2 === 0) {
				if (faceto.x - flipOffset == facefrom.x) {
					this.facePlayerDefault();
					return;
				}
			} else {
				if (faceto.x + 1 - flipOffset == facefrom.x) {
					this.facePlayerDefault();
					return;
				}
			}
		}

		const flipped = facefrom.y % 2 === 0 ? faceto.x <= facefrom.x : faceto.x < facefrom.x;
		this.creatureSprite.setDir(flipped ? -1 : 1);
	}

	/**
	 * Make creature face the default direction of its player
	 */
	facePlayerDefault() {
		this.creatureSprite.setDir(this.player.flipped ? -1 : 1);
	}

	/**
	 * Move the creature along a calculated path to the given coordinates
	 * @param{Hex} hex - Destination Hex
	 * @param{Object} opts - Optional args object
	 */
	moveTo(hex: Hex, opts) {
		const game = this.game,
			defaultOpt = {
				callback: function () {
					return true;
				},
				callbackStepIn: function () {
					return true;
				},
				animation: this.movementType() === 'flying' ? 'fly' : 'walk',
				ignoreMovementPoint: false,
				ignorePath: false,
				customMovementPoint: 0,
				overrideSpeed: 0,
				turnAroundOnComplete: true,
			};
		let path;

		opts = $j.extend(defaultOpt, opts);
		const userCallback = opts.callback;
		opts.callback = () => {
			userCallback();
			if (game.activeCreature?.id === this.id) {
				game.UI.checkAbilities();
				game.UI.selectAbility(-1);
			}
			game.signals.creature.dispatch('movementComplete', { creature: this, hex });
			// @ts-expect-error 2554
			game.onCreatureMove(this, hex); // Trigger
		};

		// Teleportation ignores moveable
		if (this.stats.moveable || opts.animation === 'teleport') {
			const x = hex.x;
			const y = hex.y;

			if (opts.ignorePath || opts.animation == 'fly') {
				path = [hex];
			} else {
				path = this.calculatePath({ x, y });
			}

			if (path.length === 0) {
				return; // Break if empty path
			}

			game.grid.xray(new Hex(0, 0, null, game)); // Clean Xray

			this.travelDist = 0;

			game.animations[opts.animation](this, path, opts);
		} else {
			game.log('This creature cannot be moved');
		}
	}

	/**
	 * Trace the path from the current position to the given coordinates
	 * @param{Point} destination: the end of the path.
	 */
	tracePath(destination: Point) {
		const path = this.calculatePath(destination); // Store path in grid to be able to compare it later

		if (path.length === 0) {
			return; // Break if empty path
		}

		// Clean the previous preview footprint before painting the new path.
		// Otherwise previewCreature() can erase penultimate adj hexes that overlap
		// the previous destination after the new path has already been drawn.
		const last = arrayUtils.last(path) as { x: number; y: number };
		const creature = this.game.retrieveCreatureStats(this.game.activeCreature.type);
		this.game.grid.previewCreature(last, creature, this.game.activePlayer);

		path.forEach((item: { x: number; y: number }) => {
			this.tracePosition({
				x: item.x,
				y: item.y,
				displayClass: 'adj',
				drawOverCreatureTiles: false,
			});
		}); // Trace path

		// Highlight final position
		this.tracePosition({
			x: last.x,
			y: last.y,
			overlayClass: 'creature moveto selected player' + this.team,
			drawOverCreatureTiles: false,
		});
	}

	tracePosition(args: TracePositionOptions) {
		const defaultArgs = {
			x: this.x,
			y: this.y,
			overlayClass: '',
			displayClass: '',
			drawOverCreatureTiles: true,
		};
		args = $j.extend(defaultArgs, args);

		for (let i = 0; i < this.size; i++) {
			let canDraw = true;

			if (!args.drawOverCreatureTiles) {
				// then check to ensure this is not a creature tile
				for (let j = 0; j < this.hexagons.length; j++) {
					if (this.hexagons[j].x == args.x - i && this.hexagons[j].y == args.y) {
						canDraw = false;
						break;
					}
				}
			}
			if (canDraw) {
				const hex = this.game.grid.hexes[args.y][args.x - i];
				this.game.grid.cleanHex(hex);
				hex.overlayVisualState(args.overlayClass);
				hex.displayVisualState(args.displayClass);
			}
		}
	}

	/**
	 * @param{Point} destination: the end of the path.
	 * @returns{Point[]} Array containing the path points.
	 */
	calculatePath(destination: Point) {
		const game = this.game;

		return search(
			game.grid.hexes[this.y][this.x],
			game.grid.hexes[destination.y][destination.x],
			this.size,
			this.id,
			this.game.grid,
		); // Calculate path
	}

	/**
	 * Return the first possible position for the creature at the given coordinates
	 * @param{number} x - Integer, Destination coordinates
	 * @param{number} y - Integer, Destination coordinates
	 * @returns{Object} New position taking into acount the size, orientation and obstacle {x,y}
	 */
	calcOffset(x: number, y: number) {
		const game = this.game,
			offset = game.players[this.team].flipped ? this.size - 1 : 0,
			mult = game.players[this.team].flipped ? 1 : -1; // For FLIPPED player

		for (let i = 0; i < this.size; i++) {
			// Try next hexagons to see if they fit
			if (x + offset - i * mult >= game.grid.hexes[y].length || x + offset - i * mult < 0) {
				continue;
			}

			if (game.grid.hexes[y][x + offset - i * mult].isWalkable(this.size, this.id)) {
				x += offset - i * mult;
				break;
			}
		}

		return {
			x: x,
			y: y,
		};
	}

	/**
	 * @returns{number} Initiative value to order the queue
	 */
	getInitiative(): number {
		// To avoid 2 identical initiative
		return this.stats.initiative * 500 - this.id;
	}

	/**
	 * @param{number} distance - Integer, Distance in hexagons
	 * @returns{Hex[]} Array of adjacent hexagons
	 */
	adjacentHexes(distance: number): Hex[] {
		const hash = hashOffsetCoords;
		const closed = new Set<number>(this.hexagons.map(hash));
		const close = (point: Point) => closed.add(hash(point));
		const isClosed = (point: Point) => closed.has(hash(point));
		const isInBounds = (point: Point) => this.game.grid.isInBounds(point);

		let atCurrRadius = this.hexagons;
		let atNextRadius = [];

		const result = [];

		for (let _ = 0; _ < distance; _++) {
			for (const point of atCurrRadius) {
				for (const neighbor of offsetNeighbors(point)) {
					if (isInBounds(neighbor) && !isClosed(neighbor)) {
						atNextRadius.push(neighbor);
						result.push(neighbor);
						close(neighbor);
					}
				}
			}
			atCurrRadius = atNextRadius;
			atNextRadius = [];
		}

		// NOTE: This is the previous implementation's sort order. Kept for consistency.
		// Sort ascending, first by row, then by column.
		result.sort((a, b) => a.x + (a.y << 16) - (b.x + (b.y << 16)));
		return result.map((point) => this.game.grid.hexAt(point.x, point.y));
	}

	/**
	 * @param {number} amount: amount of energy to restore
	 * @return {void}
	 * Restore energy up to the max limit
	 */
	recharge(amount: number, log = true) {
		const before = this.energy;
		this.energy = Math.min(this.stats.energy, this.energy + amount);
		const delta = this.energy - before;

		if (log && delta > 0) {
			this.game.log('%CreatureName' + this.id + '% recovers +' + delta + ' energy');
		}
	}

	/**
	 * Restore endurance to a creature. Will be capped against the creature's maximum
	 * endurance (this.stats.endurance).
	 * @param {*} amount Number of endurance points to restore.
	 */
	restoreEndurance(amount: number, log = true) {
		this.endurance = Math.min(this.stats.endurance, this.endurance + amount);

		if (log) {
			this.game.log('%CreatureName' + this.id + '% recovers +' + amount + ' endurance');
		}
	}

	/**
	 * Restore remaining movement to a creature. Will be capped against the creature's
	 * maximum movement (this.stats.movement).
	 *
	 * @param {*} amount Number of movement points to restore.
	 */
	restoreMovement(amount: number, log = true) {
		this.remainingMove = Math.min(this.stats.movement, this.remainingMove + amount);

		if (log) {
			this.game.log('%CreatureName' + this.id + '% recovers +' + amount + ' movement');
		}
	}

	/**
	 * @param{number} amount - Amount of health point to restore
	 */
	heal(amount: number, isRegrowth = false, log = true) {
		const game = this.game;
		// Cap health point
		amount = Math.min(amount, this.stats.health - this.health);

		if (this.health + amount < 1) {
			amount = this.health - 1; // Cap to 1hp
		}

		this.health += amount;

		// Health display Update
		this.updateHealth(isRegrowth);

		if (amount > 0) {
			if (isRegrowth) {
				this.hint('+' + amount + ' ♥', 'healing');
			} else {
				this.hint('+' + amount, 'healing');
			}

			if (log) {
				game.log('%CreatureName' + this.id + '% recovers +' + amount + ' health');
			}
		} else if (amount === 0) {
			if (isRegrowth) {
				this.hint('♦', 'msg_effects');
			} else {
				this.hint('!', 'msg_effects');
			}
		} else {
			if (isRegrowth) {
				this.hint(amount + ' ♠', 'damage');
			} else {
				this.hint(amount + '', 'damage');
			}

			if (log) {
				game.log('%CreatureName' + this.id + '% loses ' + amount + ' health');
			}
		}

		// @ts-expect-error 2554
		game.onHeal(this, amount);
	}

	/**
	 * @param{Damage} damage - Damage object
	 * @returns{Object} Contains damages dealt and if creature is killed or not
	 * TODO: Once all files in `abilities` are converted to TS, consider a more representative name for `o`
	 */
	takeDamage(
		damage: Damage,
		o?: { isFromTrap?: boolean; ignoreRetaliation?: boolean },
	): { damages?: DamageResult; kill: boolean; damageObj?: Damage } {
		const game = this.game;

		if (this.dead) {
			console.info(`${this.name} (${this.id}) is already dead, aborting takeDamage call.`);
			return { kill: false };
		}

		const defaultOpt = {
			ignoreRetaliation: false,
			isFromTrap: false,
		};

		o = $j.extend(defaultOpt, o);
		// Determine if melee attack
		damage.melee = false;
		this.adjacentHexes(1).forEach((hex) => {
			if (damage.attacker == hex.creature) {
				damage.melee = true;
			}
		});

		damage.target = this;
		damage.isFromTrap = o.isFromTrap;

		// Trigger
		game.onUnderAttack(this, damage);
		game.onAttack(damage.attacker, damage);

		// Calculation
		if (damage.status === '') {
			// Damages
			const dmg = damage.applyDamage();
			const dmgAmount = dmg.total;

			if (!isFinite(dmgAmount)) {
				// Check for Damage Errors
				this.hint('Error', 'damage');
				game.log('Oops something went wrong !');

				return {
					damages: { total: 0 },
					kill: false,
				};
			}

			this.health -= dmgAmount;
			this.health = this.health < 0 ? 0 : this.health; // Cap

			this.addFatigue(dmgAmount);

			// Display
			const nbrDisplayed = dmgAmount ? '-' + dmgAmount : 0;
			this.hint(nbrDisplayed + '', 'damage');

			if (!damage.noLog) {
				game.log('%CreatureName' + this.id + '% is hit : ' + nbrDisplayed + ' health');
			}

			// If Health is empty
			if (this.health <= 0) {
				const deathIntercepted = this.abilities.some(
					(ability) =>
						typeof ability.interceptDeath === 'function' && ability.interceptDeath(damage.attacker),
				);

				if (deathIntercepted) {
					this.updateHealth();
					game.UI.updateFatigue();
					game.UI.checkAbilities();

					if (!o.ignoreRetaliation) {
						// @ts-expect-error 2554
						game.onDamage(this, damage);
					}

					return {
						damages: dmg,
						damageObj: damage,
						kill: false,
					};
				}

				this.die(damage.attacker);

				return {
					damages: dmg,
					damageObj: damage,
					kill: true,
				}; // Killed
			}

			// Effects
			damage.effects.forEach((effect) => {
				this.addEffect(effect);
			});

			// Unfreeze if taking non-zero damage and not a Cryostasis freeze.
			if (dmgAmount > 0 && !this.isInCryostasis()) {
				this.status.frozen = false;
			}

			// Health display Update
			// Note: update health after adding effects as some effects may affect
			// health display
			this.updateHealth();
			game.UI.updateFatigue();
			/* Some of the active creature's abilities may become active/inactive depending
			on new health/endurance values. */
			game.UI.checkAbilities();

			// Trigger
			if (!o.ignoreRetaliation) {
				// @ts-expect-error 2554
				game.onDamage(this, damage);
			}

			return {
				damages: dmg,
				damageObj: damage,
				kill: false,
			}; // Not Killed
		} else {
			if (damage.status == 'Dodged') {
				// If dodged
				if (!damage.noLog) {
					game.log('%CreatureName' + this.id + '% dodged the attack');
				}
			}

			if (damage.status == 'Shielded') {
				// If Shielded
				if (!damage.noLog) {
					game.log('%CreatureName' + this.id + '% shielded the attack');
				}
			}

			if (damage.status == 'Disintegrated') {
				// If Disintegrated
				if (!damage.noLog) {
					game.log('%CreatureName' + this.id + '% has been disintegrated');
				}
				this.die(damage.attacker);
			}

			// Hint
			this.hint(damage.status, 'damage');
		}

		return {
			damageObj: damage,
			kill: false,
		}; // Not killed
	}

	updateHealth(noAnimBar = false) {
		const game = this.game;

		if (this == game.activeCreature && !noAnimBar) {
			game.UI.healthBar.animSize(this.health / this.stats.health);
		}

		// Dark Priest plasma shield when inactive
		if (this.isDarkPriest()) {
			if (this.hasCreaturePlayerGotPlasma() && this !== game.activeCreature) {
				this.displayPlasmaShield();
			} else {
				this.displayHealthStats();
			}
		} else {
			this.displayHealthStats();
		}
	}

	displayHealthStats() {
		this.creatureSprite.setHealth(this.health, this.isFrozen() ? 'frozen' : 'health');
	}

	displayPlasmaShield() {
		this.creatureSprite.setHealth(this.player.plasma, 'plasma');
	}

	hasCreaturePlayerGotPlasma() {
		return this.player.plasma > 0;
	}

	addFatigue(dmgAmount: number) {
		if (!this.stats.fatigueImmunity) {
			this.endurance -= dmgAmount;
			this.endurance = this.endurance < 0 ? 0 : this.endurance; // Cap
		}

		this.game.UI.updateFatigue();
	}

	addEffect(
		effect: Effect,
		specialString?: string,
		specialHint?: string,
		disableLog = false,
		disableHint = false,
	) {
		const game = this.game;

		if (!effect.stackable && this.findEffect(effect.name).length !== 0) {
			return false;
		}

		if (this.effects.includes(effect)) {
			return false;
		}

		effect.target = this;
		this.effects.push(effect);

		game.onEffectAttach(this, effect);

		this.updateAlteration();

		if (effect.name !== '') {
			if (!disableHint) {
				if (specialHint || effect.specialHint) {
					this.hint(specialHint, 'msg_effects');
				} else {
					this.hint(effect.name, 'msg_effects');
				}
			}

			if (!disableLog) {
				if (specialString) {
					game.log(specialString);
				} else {
					game.log('%CreatureName' + this.id + '% is affected by ' + effect.name);
				}
			}
		}
	}

	/** replaceEffect
	 * Add effect, but if the effect is already attached, replace it with the new
	 * effect.
	 * Note that for stackable effects, this is the same as addEffect()
	 */
	replaceEffect(effectToAdd: Effect) {
		if (!effectToAdd.stackable && this.findEffect(effectToAdd.name).length !== 0) {
			this.removeEffect(effectToAdd.name);
		}

		this.addEffect(effectToAdd);
	}

	/** removeEffect
	 * Remove an effect by name
	 */
	removeEffect(effectName: string) {
		const effectToRemove = this.effects.find((effect) => effect.name === effectName);

		if (!effectToRemove) {
			return;
		}

		effectToRemove.deleteEffect();
	}

	hint(text: string, hintType: CreatureHintType) {
		this.creatureSprite.hint(text, hintType);
	}

	clearHints(hintTypes: CreatureHintType[] = ['confirm', 'no_action']) {
		this.creatureSprite.clearHints(hintTypes);
	}

	stopNoActionHintBounce() {
		this.creatureSprite.stopNoActionHintBounce();
	}

	fadeOutNoActionHints() {
		this.creatureSprite.fadeOutNoActionHints();
	}

	/**
	 * Update the stats taking into account the effects' alteration
	 */
	updateAlteration() {
		this.stats = { ...this.baseStats };

		const buffDebuffArray = [...this.effects, ...this.dropCollection];

		buffDebuffArray.forEach((buff) => {
			$j.each(buff.alterations, (key, value) => {
				if (typeof value === 'string') {
					// Multiplication Buff
					if (value.match(/\*/)) {
						this.stats[key] = eval(this.stats[key] + value);
					}

					// Division Debuff
					if (value.match(/\//)) {
						this.stats[key] = eval(this.stats[key] + value);
					}
				}

				// Usual Buff/Debuff
				if (typeof value == 'number') {
					this.stats[key] += value;
				}
				if (key == 'movement' && !(buff instanceof Drop)) {
					// Only restore movement points for effects, not drops
					// Drops have their own explicit restoration logic in drop.ts
					this.remainingMove += value;
				}

				// Boolean Buff/Debuff
				if (typeof value == 'boolean') {
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(this.stats as any)[key] = value;
				}
			});
		});

		// Maximum stat pools cannot be lower than 1.
		this.stats.health = Math.max(this.stats.health, 1);
		this.stats.endurance = Math.max(this.stats.endurance, 1);
		this.stats.energy = Math.max(this.stats.energy, 1);
		this.stats.movement = Math.max(this.stats.movement, 1);

		// These stats cannot exceed their maximum values.
		this.health = Math.min(this.health, this.stats.health);
		this.endurance = Math.min(this.endurance, this.stats.endurance);
		this.energy = Math.min(this.energy, this.stats.energy);
		this.remainingMove = Math.min(this.remainingMove, this.stats.movement);
	}

	/**
	 * Play kill animation. Remove creature from queue and from hexes.
	 * @param{Creature | {player:Player}} killerCreature - Killer of this creature
	 */
	die(killerCreature: Creature | { player: Player }) {
		const game = this.game;

		this.dead = true;

		// Triggers
		// @ts-expect-error 2554
		game.onCreatureDeath(this);

		// BRB state: Gumble's upgraded Gooey Body intercepts death
		if (this._brbActive && this._brbState) {
			this._brbActive = false;
			this._brbSpent = true; // prevent Gooey Body re-triggering on follow-up death
			this.dead = false;
			this._brbState.killer = killerCreature;

			// If the goo trap is destroyed while Gumble is in BRB state (e.g. another
			// trap overwrites it), Gumble dies immediately with full score/queue update.
			// The destroyer (the creature whose trap caused the overwrite) becomes the killer.
			this._brbState.gooTrap.onDestroyFn = (destroyer?: Creature) => {
				if (!this._brbState) return; // already resolved (revived or died)
				const savedState = this._brbState;
				this._brbState = null;
				this.die(destroyer ?? savedState.killer);
			};

			// Clear the hex first so the trap spot is immediately walkable
			// and pathfinding/queue-display sees the correct state.
			this.cleanHex();

			// Play melt animation — Gumble appears to die but the trap will
			// resurrect him. No score, no 'is dead' log. Queue avatar shows BRB.
			const brbAnimOpts = {
				callback: () => {
					game.updateQueueDisplay();
					game.grid.updateDisplay();
				},
				flipped: killerCreature instanceof Creature ? this.pos.x - killerCreature.pos.x < 0 : false,
			};
			game.animations.melt(this, brbAnimOpts);
			game.log('%CreatureName' + this.id + '% will be right back...');
			game.updateQueueDisplay();
			game.grid.updateDisplay();
			if (game.activeCreature === this) {
				game.nextCreature();
				return;
			}
			game.activeCreature.queryMove();
			return;
		}

		game.log('%CreatureName' + this.id + '% is dead');
		this.killer = killerCreature.player;
		const isDeny = this.killer.flipped == this.player.flipped;

		// Drop item
		if (game.unitDrops == 1 && this.drop) {
			const offsetX = this.player.flipped ? this.x - this.size + 1 : this.x;
			const { name, ...alterations } = this.drop;
			new Drop(name, alterations, offsetX, this.y, game);
			// If a creature is already standing on the drop hex (e.g. in the BRB scenario
			// where the goo trap was overwritten and Gumble dies under them), give them
			// the drop immediately rather than waiting for movement.
			const dropHex = game.hexAt(offsetX, this.y);
			if (dropHex) {
				const occupant = dropHex.creature;
				if (occupant && occupant !== this && !occupant.dead) {
					occupant.pickupDrop();
				}
			}
		}

		if (!game.firstKill && !isDeny) {
			// First Kill
			this.killer.score.push({
				type: 'firstKill',
			});
			game.firstKill = true;
		}

		if (this.isDarkPriest()) {
			// If Dark Priest
			if (isDeny) {
				// TEAM KILL (DENY)
				this.killer.score.push({
					type: 'deny',
					creature: this,
				});
			} else {
				// Humiliation
				this.killer.score.push({
					type: 'humiliation',
					player: this.team,
				});
			}
		}

		if (!this.undead) {
			// Only if not undead
			if (isDeny) {
				// TEAM KILL (DENY)
				this.killer.score.push({
					type: 'deny',
					creature: this,
				});
			} else {
				// KILL
				this.killer.score.push({
					type: 'kill',
					creature: this,
				});
			}
		}

		if (this.player.isAnnihilated()) {
			// Remove humiliation as annihilation is an upgrade
			const total = this.killer.score.length;
			for (let i = 0; i < total; i++) {
				const s = this.killer.score[i];
				if (s.type == 'humiliation') {
					if (s.player == this.team) {
						this.killer.score.splice(i, 1);
					}

					break;
				}
			}
			// ANNIHILATION
			this.killer.score.push({
				type: 'annihilation',
				player: this.team,
			});
		}

		if (this.isDarkPriest()) {
			this.player.deactivate(); // Here because of score calculation
		}

		// Kill animation
		const opts = {
			callback: () => {
				this.destroy();
			},
			flipped: false,
		};

		// Check whether or not to flip the animation
		if (killerCreature instanceof Creature) {
			if (this.pos.x - killerCreature.pos.x < 0) {
				opts.flipped = true;
			}
		}

		const customDeathAnimation = (this as CreatureRuntimeFlags).deathAnimationType;
		if (customDeathAnimation === 'shatterDown') {
			game.animations.shatterDown(this, opts);
		} else if (customDeathAnimation === 'melt') {
			game.animations.melt(this, opts);
		} else {
			game.animations.death(this, opts);
		}
		this.cleanHex();

		game.updateQueueDisplay();
		game.grid.updateDisplay();

		if (game.activeCreature === this) {
			game.nextCreature();
			return;
		} // End turn if current active creature die

		// As hex occupation changes, path must be recalculated for the current creature not the dying one
		game.activeCreature.queryMove();
	}

	isFatigued() {
		return this.endurance === 0;
	}

	isFragile() {
		return this.stats.endurance === 1;
	}

	/**
	 * Shortcut convenience function to grid.getHexMap
	 */
	getHexMap(map: AugmentedMatrix, invertFlipped: boolean) {
		const x = (this.player.flipped ? !invertFlipped : invertFlipped)
			? this.x + 1 - this.size
			: this.x;
		return this.game.grid.getHexMap(
			x,
			this.y - map.origin[1],
			0 - map.origin[0],
			this.player.flipped ? !invertFlipped : invertFlipped,
			map,
		);
	}

	findEffect(name) {
		const ret = [];

		this.effects.forEach((effect) => {
			if (effect.name == name) {
				ret.push(effect);
			}
		});

		return ret;
	}

	// Make units transparent
	xray(enable: boolean, referenceCreature?: Creature | Creature[]) {
		this.creatureSprite.xray(enable, referenceCreature);
	}

	clearXrayImmediately() {
		this.creatureSprite.clearXrayImmediately();
	}

	get isXrayed(): boolean {
		return this.creatureSprite?.isXrayed ?? false;
	}

	pickupDrop() {
		getPointFacade()
			.getDropsAt(this)
			.forEach((drop) => {
				if (!drop.pickedUp) {
					drop.pickup(this);
					drop.pickedUp = true; // Prevent multiple pickups
				}
			});
	}

	/**
	 * Get movement type for this creature
	 */
	movementType(): string {
		const totalAbilities = this.abilities.length;

		// If the creature has an ability that modifies movement type, use that,
		// otherwise use the creature's base movement type
		for (let i = 0; i < totalAbilities; i++) {
			if (typeof this.abilities[i].movementType === 'function') {
				return this.abilities[i].movementType();
			}
		}

		return this._movementType;
	}

	/**
	 * Is this unit a Dark Priest?
	 */
	isDarkPriest(): boolean {
		return this.type === '--';
	}

	/**
	 * Does the creature have the Frozen status? @see status.frozen
	 */
	isFrozen(): boolean {
		return this.status.frozen;
	}

	/**
	 * Does the creature have the Cryostasis status? @see status.cryostasis
	 */
	isInCryostasis(): boolean {
		return this.isFrozen() && this.status.cryostasis;
	}

	/**
	 * Same as the "Frozen" status, but with a different name.
	 *
	 * TODO: Refactor to a generic "skip turn" status that can be customised.
	 */
	isDizzy(): boolean {
		return this.status.dizzy;
	}

	/**
	 * Freeze a creature, skipping its next turn. @see status.frozen
	 */
	freeze(cryostasis = false) {
		this.status.frozen = true;

		if (cryostasis) {
			this.status.cryostasis = true;
		}

		// Update the health box under the creature cardboard with frozen effect.
		this.updateHealth();
		// Show frozen fatigue text effect in queue.
		this.game.UI.updateFatigue();

		this.game.signals.creature.dispatch('frozen', { creature: this, cryostasis });
	}

	get fatigueText(): string {
		let result = '';
		if (this._brbState) {
			const occupant = this._brbState.gooTrap?.hex?.creature;
			const text = occupant && occupant !== this ? 'AFK' : 'BRB';
			this.#fatigueText = text;
			return text;
		} else if (this.player.hasLost) {
			result = 'Dazzled';
		} else if (this.isFrozen()) {
			result = this.isInCryostasis() ? 'Cryostasis' : 'Frozen';
		} else if (this.isDizzy()) {
			result = 'Dizzy';
		} else if (this.materializationSickness) {
			result = 'Sickened';
		} else if (this.protectedFromFatigue || this.stats.fatigueImmunity) {
			result = 'Protected';
		} else if (this.isFragile()) {
			result = 'Fragile';
			// Display message if the creature has first become fragile

			// TODO: This isn't necessarily the moment the creature has
			// become fragile. The code will run twice if, e.g.,
			// the creature is fragile, then fragile and dizzy, then fragile
			if (this.#fatigueText !== result) {
				this.game.log('%CreatureName' + this.id + '% has become fragile');
			}
		} else if (this.isFatigued()) {
			result = 'Fatigued';
		} else {
			result = this.endurance + '/' + this.stats.endurance;
		}

		if (this.isDarkPriest()) {
			// If Dark Priest
			this.abilities[0].require(); // Update protectedFromFatigue
		}

		this.#fatigueText = result;
		return result;
	}

	destroy() {
		this.creatureSprite.destroy();
		// NOTE: If this was a temp creature remove it from game.creatures.
		// Dead creatures are supposed to stay in game.creatures.
		// We null out the slot rather than filtering (which would shift indices and
		// break the game.creatures[id] lookup used throughout the codebase).
		if (this.temp) {
			this.game.creatures[this.id] = undefined;
		}
	}
}

class CreatureSprite {
	private _creature: Creature;
	private _group: Phaser.Group;
	private _sprite: Phaser.Sprite;
	private _hintGrp: Phaser.Group;

	private _healthIndicatorGroup: Phaser.Group;
	private _healthIndicatorSprite: Phaser.Sprite;
	private _healthIndicatorText: Phaser.Text;
	private _healthIndicatorTween: Phaser.Tween | null;
	private _noActionHintElements: Array<Phaser.Text | Phaser.Sprite> = [];
	private _noActionHintGroup: Phaser.Group | null = null;
	private _noActionHintTween: Phaser.Tween | null = null;
	private _healthBounceOffset = 0; // y-offset driven by the bounce tween
	private _healthUiGroup: Phaser.Group; // elevated layer for active/hovered indicators
	private _healthInUiGroup = false; // whether the indicator is currently elevated

	private _phaser: Phaser.Game;
	private _frameInfo: { originX: number; originY: number };
	private _creatureSize: number;
	private _creatureTeam: PlayerID;

	private _isXray = false;
	get isXrayed(): boolean {
		return this._isXray;
	}
	private _xrayAlpha = 0; // current effect intensity (0 = off, 1 = full)
	get xrayAlpha(): number {
		return this._xrayAlpha;
	}
	private _xrayTargetAlpha = 0; // target intensity for fade animation
	private _xrayBmd: Phaser.BitmapData | null = null;
	private _originalTextureKey: string;
	private _xrayOriginalSrc: CanvasImageSource | null = null;
	private _xrayRefCreatures: Creature[] = []; // all ref creatures whose shape we cut out
	private _xrayScratch: HTMLCanvasElement | null = null; // Part B scratch
	private _xrayMask: HTMLCanvasElement | null = null; // union of all ref shapes

	constructor(creature: Creature) {
		const { game, player, type, team, display, size, id, health } = creature;
		const dir = player.flipped ? -1 : 1;
		const phaser = game.Phaser;

		this._creature = creature;
		this._phaser = phaser;
		this._creatureSize = size;
		this._creatureTeam = team;
		this._frameInfo = { originX: display['offset-x'], originY: display['offset-y'] };

		const group: Phaser.Group = phaser.add.group(game.grid.creatureGroup, 'creatureGrp_' + id);
		group.alpha = 0;

		const isDarkPriest = type === '--';
		const darkPriestColorOrEmpty = isDarkPriest ? ' ' + creature.player.color : '';

		// Adding sprite
		const spriteKey = creature.name + darkPriestColorOrEmpty;
		const sprite = group.create(0, 0, spriteKey);
		sprite.anchor.setTo(0.5, 1);
		// Placing sprite
		sprite.x =
			(!player.flipped
				? display['offset-x']
				: HEX_WIDTH_PX * size - sprite.texture.width - display['offset-x']) +
			sprite.texture.width / 2;
		sprite.y = display['offset-y'] + sprite.texture.height;

		if (creature.name === 'Infernal') {
			game.animations.initInfernalCardboardEffect(creature, sprite);
		}

		// Hint Group
		const hintGrp = phaser.add.group(group, 'creatureHintGrp_' + id);
		hintGrp.x = 0.5 * HEX_WIDTH_PX * size;
		hintGrp.y = -sprite.texture.height + 5;

		const healthIndicatorGroup = phaser.add.group(group, 'creatureHealthGrp_' + id);

		const healthIndicatorSprite = healthIndicatorGroup.create(
			player.flipped ? 19 : 19 + HEX_WIDTH_PX * (size - 1),
			49,
			'p' + team + '_health',
		);

		const healthIndicatorText = phaser.add.text(
			player.flipped ? HEX_WIDTH_PX * 0.5 : HEX_WIDTH_PX * (size - 0.5),
			63,
			health,
			{
				font: 'bold 15pt Play',
				fill: '#fff',
				align: 'center',
				stroke: '#000',
				strokeThickness: 6,
			},
		);
		healthIndicatorText.anchor.setTo(0.5, 0.5);
		healthIndicatorGroup.add(healthIndicatorText);
		healthIndicatorGroup.visible = false;

		this._group = group;
		this._sprite = sprite;

		this._hintGrp = hintGrp;

		this._healthIndicatorGroup = healthIndicatorGroup;
		this._healthIndicatorSprite = healthIndicatorSprite;
		this._healthIndicatorText = healthIndicatorText;
		this._healthIndicatorTween = undefined;
		this._healthUiGroup = game.grid.healthIndicatorUiGroup;

		this._originalTextureKey = spriteKey;

		// Install a per-frame update hook on the group. Phaser calls group.update()
		// every step AFTER tweens have run, which is exactly when we need to redraw
		// the xray BitmapData so the cutout tracks movement smoothly.
		const _groupUpdate = this._group.update.bind(this._group);
		const XRAY_FADE_RATE = 0.08; // ~160 ms fade at 60 fps
		this._group.update = () => {
			_groupUpdate();
			game.animations.tickInfernalCardboardEffect(this._creature);
			// Animate xray alpha toward target
			if (this._xrayAlpha < this._xrayTargetAlpha) {
				this._xrayAlpha = Math.min(this._xrayTargetAlpha, this._xrayAlpha + XRAY_FADE_RATE);
			} else if (this._xrayAlpha > this._xrayTargetAlpha) {
				this._xrayAlpha = Math.max(this._xrayTargetAlpha, this._xrayAlpha - XRAY_FADE_RATE);
			}
			// Sync health indicator world position when it lives in the elevated UI group
			if (this._healthInUiGroup) {
				this._healthIndicatorGroup.x = this._group.x;
				this._healthIndicatorGroup.y = this._group.y + this._healthBounceOffset;
			}
			// Fade health indicator group in sync with the xray sprite effect.
			// The health group is a separate Phaser object rendered on top of the
			// sprite, so sprite BitmapData tricks can't hide it — we must set its
			// alpha directly so the reference creature's badge shows through.
			const hiAlpha = 1.0 - (1.0 - 0.2) * this._xrayAlpha;
			this._healthIndicatorGroup.alpha = hiAlpha;
			if (this._xrayBmd && this._xrayRefCreatures.length > 0) {
				if (this._xrayAlpha <= 0 && this._xrayTargetAlpha === 0) {
					// Fade-out complete — restore original texture
					this._finalizeXrayOff();
				} else {
					this._drawXrayBmd(this._xrayRefCreatures, this._xrayBmd);
				}
			}
		};

		this.setHex(creature.hexagons[size - 1]);
		this.setDir(dir);
	}

	// NOTE: This is the old API exposed by Creature.
	// Kept for compatibility, but usage should be phased out.
	// TODO: Prefer using CreatureSprite methods and remove these when possible.
	get grp() {
		return this._group;
	}
	get sprite() {
		return this._sprite;
	}

	// TODO: Refactor
	// This currently has one user.
	// Refactoring into some combination of left, right, top, bottom, centerX, centerY would be welcome.
	get legacyProjectileEmissionPoint() {
		return { x: this._group.x, y: this._group.y };
	}

	private _promisifyTween(
		target: object,
		tweenProperties: Record<string, number>,
		durationMS = 1000,
		easing = Phaser.Easing.Linear.None,
	): Promise<CreatureSprite> {
		const tween = this._phaser.add.tween(target).to(tweenProperties, durationMS, easing);
		const promise: Promise<CreatureSprite> = new Promise((resolve) => {
			tween.onComplete.add(() => resolve(this));
		});
		tween.start();
		return promise;
	}

	setAlpha(a: number, durationMS = 0): Promise<CreatureSprite> {
		if (durationMS === 0 || this._group.alpha === a) {
			this._group.alpha = a;
			return new Promise((resolve) => {
				resolve(this);
			});
		}
		return this._promisifyTween(this._group, { alpha: a }, durationMS);
	}

	setAngle(a: number, durationMS = 0): Promise<CreatureSprite> {
		if (durationMS === 0 || this._group.angle === a) {
			this._group.angle = a;
			return new Promise((resolve) => {
				resolve(this);
			});
		} else {
			return this._promisifyTween(this._group, { angle: a }, durationMS);
		}
	}

	setHex(h: Hex, durationMS = 0): Promise<CreatureSprite> {
		return this.setPx(h.displayPos, durationMS);
	}

	setPx(pos: { x: number; y: number }, durationMS = 0): Promise<CreatureSprite> {
		if (durationMS === 0) {
			this._group.position.set(pos.x, pos.y);
			return new Promise((resolve) => {
				resolve(this);
			});
		} else {
			return this._promisifyTween(this._group, pos, durationMS);
		}
	}

	setDir(dir: 1 | -1) {
		this._sprite.scale.setTo(dir, 1);

		this._sprite.x =
			(dir === 1
				? this._frameInfo.originX
				: HEX_WIDTH_PX * this._creatureSize -
				  this._sprite.texture.width -
				  this._frameInfo.originX) +
			this._sprite.texture.width / 2;
		this._healthIndicatorSprite.x = dir === -1 ? 19 : 19 + HEX_WIDTH_PX * (this._creatureSize - 1);
		this._healthIndicatorText.x =
			dir === -1 ? HEX_WIDTH_PX * 0.5 : HEX_WIDTH_PX * (this._creatureSize - 0.5);
	}

	xray(enable: boolean, referenceCreature?: Creature | Creature[]) {
		if (!enable && !this._isXray && this._xrayTargetAlpha === 0) return;
		if (enable && referenceCreature) {
			const nextRefCreatures = Array.isArray(referenceCreature)
				? referenceCreature
				: [referenceCreature];
			const hasSameReferences =
				this._isXray &&
				this._xrayRefCreatures.length === nextRefCreatures.length &&
				this._xrayRefCreatures.every((reference, index) => reference === nextRefCreatures[index]);
			if (hasSameReferences) {
				return;
			}
			this._isXray = true;
			this._xrayTargetAlpha = 1;
			// Replace the active hover reference instead of accumulating old ones.
			const alpha = this._xrayAlpha;
			const targetAlpha = this._xrayTargetAlpha;
			this._xrayScratch = null;
			this._xrayMask = null;
			this._xrayRefCreatures = nextRefCreatures.slice();
			this._xrayAlpha = alpha;
			this._xrayTargetAlpha = targetAlpha;
			this._isXray = true;
			this._buildXrayTexture(this._xrayRefCreatures);
		} else if (enable) {
			// Ignore legacy calls that try to enable xray without a reference target.
			// The pixel-mask implementation needs a reference creature silhouette;
			// treating this as "off" would clear valid active/hover xray states.
			return;
		} else {
			this._isXray = false;
			this._xrayTargetAlpha = 0;
			// Update hook fades out then calls _finalizeXrayOff automatically
		}
	}

	clearXrayImmediately() {
		this._isXray = false;
		this._clearXrayTexture();
	}

	/** Immediately snaps xray alpha to 0 and frees all resources. */
	private _clearXrayTexture() {
		this._xrayAlpha = 0;
		this._xrayTargetAlpha = 0;
		this._finalizeXrayOff();
	}

	/** Restores the original texture and frees all xray canvas resources. */
	private _finalizeXrayOff() {
		this._healthIndicatorGroup.alpha = 1;
		this._xrayRefCreatures = [];
		this._xrayScratch = null;
		this._xrayMask = null;
		if (this._xrayBmd && this._isDrawableImageSource(this._xrayOriginalSrc)) {
			const bmd = this._xrayBmd;
			const ctx = bmd.context;
			ctx.clearRect(0, 0, bmd.width, bmd.height);
			const drewOriginal = this._drawSpriteFrame(
				ctx,
				this._sprite,
				this._xrayOriginalSrc,
				0,
				0,
				bmd.width,
				bmd.height,
			);
			if (!drewOriginal) {
				this._sprite.loadTexture(this._originalTextureKey);
				return;
			}
			bmd.dirty = true;
			bmd.update();

			if (this._sprite.texture.baseTexture.source !== (bmd.canvas as CanvasImageSource)) {
				this._sprite.loadTexture(bmd);
			}
			return;
		}

		this._sprite.loadTexture(this._originalTextureKey);
	}

	private _buildXrayTexture(refCreatures: Creature[]) {
		const otw = this._sprite.texture.width;
		const oth = this._sprite.texture.height;

		// Capture the original image source BEFORE swapping the texture to the BitmapData.
		// After loadTexture(bmd), sprite.texture.baseTexture.source would be the BitmapData
		// canvas itself, causing a circular self-draw on subsequent frames.
		if (!this._isDrawableImageSource(this._xrayOriginalSrc)) {
			this._xrayOriginalSrc = this._resolveSpriteDrawSource(this._sprite);
			if (!this._xrayOriginalSrc) {
				this._clearXrayTexture();
				return;
			}
		}
		this._xrayRefCreatures = refCreatures.slice(); // copy to avoid external mutation

		const scratch = document.createElement('canvas');
		scratch.width = otw;
		scratch.height = oth;
		this._xrayScratch = scratch;

		const mask = document.createElement('canvas');
		mask.width = otw;
		mask.height = oth;
		this._xrayMask = mask;

		let bmd = this._xrayBmd;
		if (!bmd || bmd.width !== otw || bmd.height !== oth) {
			if (bmd) {
				bmd.destroy();
			}
			bmd = this._phaser.add.bitmapData(otw, oth);
		}
		this._xrayBmd = bmd;

		this._drawXrayBmd(this._xrayRefCreatures, bmd);
		if (this._sprite.texture.baseTexture.source !== (bmd.canvas as CanvasImageSource)) {
			this._sprite.loadTexture(bmd);
		}
	}

	/**
	 * Draw the sprite's current texture frame from its base image source.
	 * Handles atlases/cropped textures by using frame coordinates.
	 */
	private _drawSpriteFrame(
		ctx: CanvasRenderingContext2D,
		sprite: Phaser.Sprite,
		src: CanvasImageSource,
		dx: number,
		dy: number,
		dw: number,
		dh: number,
	): boolean {
		if (!this._isDrawableImageSource(src) || dw <= 0 || dh <= 0) {
			return false;
		}

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const texture: any = sprite.texture as any;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const frame: any = texture.crop || texture.frame || { x: 0, y: 0, width: dw, height: dh };
		const sx = typeof frame.x === 'number' ? frame.x : 0;
		const sy = typeof frame.y === 'number' ? frame.y : 0;
		const sw = typeof frame.width === 'number' ? frame.width : dw;
		const sh = typeof frame.height === 'number' ? frame.height : dh;
		if (sw <= 0 || sh <= 0) {
			return false;
		}

		ctx.drawImage(src, sx, sy, sw, sh, dx, dy, dw, dh);
		return true;
	}

	private _isDrawableImageSource(src: unknown): src is CanvasImageSource {
		if (!src) {
			return false;
		}

		if (typeof HTMLImageElement !== 'undefined' && src instanceof HTMLImageElement) {
			return true;
		}
		if (typeof SVGImageElement !== 'undefined' && src instanceof SVGImageElement) {
			return true;
		}
		if (typeof HTMLCanvasElement !== 'undefined' && src instanceof HTMLCanvasElement) {
			return true;
		}
		if (typeof HTMLVideoElement !== 'undefined' && src instanceof HTMLVideoElement) {
			return true;
		}
		if (typeof OffscreenCanvas !== 'undefined' && src instanceof OffscreenCanvas) {
			return true;
		}
		if (typeof ImageBitmap !== 'undefined' && src instanceof ImageBitmap) {
			return true;
		}
		const maybeVideoFrame =
			typeof window !== 'undefined'
				? (window as unknown as Record<string, unknown>).VideoFrame
				: undefined;
		if (
			typeof maybeVideoFrame === 'function' &&
			src instanceof (maybeVideoFrame as typeof Function)
		) {
			return true;
		}

		return false;
	}

	private _resolveSpriteDrawSource(sprite: Phaser.Sprite): CanvasImageSource | null {
		const src = sprite?.texture?.baseTexture?.source;
		return this._isDrawableImageSource(src) ? src : null;
	}

	/**
	 * Redraws the xray BitmapData in-place using the current creature positions.
	 * Called once on initial build and then every frame so the cutout tracks movement.
	 *
	 * The mask is the UNION of all refCreatures shapes:
	 *   - Pixels covered by any ref creature : XRAY_OVERLAP_OPACITY alpha (see-through)
	 *   - All other pixels                   : 1.0 alpha (fully opaque)
	 */
	private _drawXrayBmd(refCreatures: Creature[], bmd: Phaser.BitmapData) {
		const oSprite = this._sprite;
		const otw = bmd.width;
		const oth = bmd.height;
		const oFlipped = oSprite.scale.x < 0;
		// Guard: skip if sprite hasn't been added to display tree yet
		if (!oSprite.worldTransform) {
			return;
		}
		const oBounds = oSprite.getBounds();
		const oLeft = oBounds.left;
		const oTop = oBounds.top;
		const oScaleX = oBounds.width > 0 ? otw / oBounds.width : 1;
		const oScaleY = oBounds.height > 0 ? oth / oBounds.height : 1;

		const oSrc = this._xrayOriginalSrc;
		if (!this._isDrawableImageSource(oSrc)) {
			this._clearXrayTexture();
			return;
		}
		const ctx = bmd.context;
		const scratch = this._xrayScratch as HTMLCanvasElement;
		const sctx = scratch.getContext('2d') as CanvasRenderingContext2D;

		// Build a union mask on scratch2: all ref creature shapes OR'd together
		// We keep a second scratch for the union mask (reuse _xrayScratch2).
		const mask = this._xrayMask as HTMLCanvasElement;
		const mctx = mask.getContext('2d') as CanvasRenderingContext2D;
		mctx.clearRect(0, 0, otw, oth);

		for (const refCreature of refCreatures) {
			const refSprite = refCreature.sprite;
			// Guard: skip ref creatures whose sprites haven't been added to display tree yet
			if (!refSprite.worldTransform) {
				continue;
			}
			const rBounds = refSprite.getBounds();
			const rtw = Math.max(1, Math.round(rBounds.width * oScaleX));
			const rth = Math.max(1, Math.round(rBounds.height * oScaleY));
			const relX = Math.round((rBounds.left - oLeft) * oScaleX);
			const relY = Math.round((rBounds.top - oTop) * oScaleY);
			const drawX = oFlipped ? Math.round(otw - relX - rtw) : relX;
			const rFlipped = refSprite.scale.x < 0;
			const flipRef = oFlipped !== rFlipped;
			const rSrc = this._resolveSpriteDrawSource(refSprite);
			if (!rSrc) {
				continue;
			}

			// source-over accumulates all ref shapes into the mask (union)
			mctx.globalCompositeOperation = 'source-over';
			if (!flipRef) {
				this._drawSpriteFrame(mctx, refSprite, rSrc, drawX, relY, rtw, rth);
			} else {
				mctx.save();
				mctx.translate(drawX + rtw, relY);
				mctx.scale(-1, 1);
				this._drawSpriteFrame(mctx, refSprite, rSrc, 0, 0, rtw, rth);
				mctx.restore();
			}
		}

		const XRAY_OVERLAP_OPACITY = 0.2;

		// --- Part A: overlap region — obstructor at reduced alpha, clipped to union mask ---
		ctx.clearRect(0, 0, otw, oth);
		ctx.save();
		ctx.globalAlpha = 1.0 - (1.0 - XRAY_OVERLAP_OPACITY) * this._xrayAlpha;
		if (!this._drawSpriteFrame(ctx, oSprite, oSrc, 0, 0, otw, oth)) {
			ctx.restore();
			this._clearXrayTexture();
			return;
		}
		ctx.globalAlpha = 1.0;
		ctx.globalCompositeOperation = 'destination-in';
		ctx.drawImage(mask, 0, 0);
		ctx.restore();

		// --- Part B: non-overlap at full alpha — obstructor minus union mask ---
		sctx.clearRect(0, 0, otw, oth);
		sctx.globalCompositeOperation = 'source-over';
		if (!this._drawSpriteFrame(sctx, oSprite, oSrc, 0, 0, otw, oth)) {
			this._clearXrayTexture();
			return;
		}
		sctx.globalCompositeOperation = 'destination-out';
		sctx.drawImage(mask, 0, 0);

		// Composite Part B on top of Part A
		ctx.drawImage(scratch, 0, 0);

		bmd.dirty = true;
		bmd.update();
	}

	setHealth(number: number | string, type: HealthBubbleType) {
		// Support both Phaser Text API and test doubles without setText()
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const textObj: any = (this as any)._healthIndicatorText;
		if (textObj && typeof textObj.setText === 'function') {
			textObj.setText(String(number));
		} else if (textObj) {
			textObj.text = String(number);
		}
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const spriteObj: any = (this as any)._healthIndicatorSprite;
		if (spriteObj && typeof spriteObj.loadTexture === 'function') {
			spriteObj.loadTexture(`p${this._creatureTeam}_${type}`);
		}
	}

	showHealth(enable: boolean) {
		this._healthIndicatorGroup.visible = enable;
	}

	/**
	 * Reparent the health indicator into (or out of) the top-most UI group so it
	 * renders above all creature sprites. Call with true when the creature becomes
	 * active or hovered; false when it returns to normal.
	 */
	elevateHealth(enable: boolean) {
		if (enable && !this._healthInUiGroup) {
			this._healthUiGroup.add(this._healthIndicatorGroup);
			this._healthIndicatorGroup.x = this._group.x;
			this._healthIndicatorGroup.y = this._group.y + this._healthBounceOffset;
			this._healthInUiGroup = true;
		} else if (!enable && this._healthInUiGroup) {
			this._group.add(this._healthIndicatorGroup);
			this._healthIndicatorGroup.x = 0;
			this._healthIndicatorGroup.y = 0;
			this._healthInUiGroup = false;
		}
	}

	setHealthBounce(enable: boolean) {
		if (enable) {
			const bounceHeight = 10;
			const durationMS = 350;

			if (this._healthIndicatorTween && this._healthIndicatorTween.isRunning) {
				return;
			}

			if (!this._healthIndicatorTween || !this._healthIndicatorTween.isRunning) {
				const bounceTgt = { offset: -bounceHeight };
				const bounceSrc = { offset: 0 };
				this._healthBounceOffset = 0;

				this._healthIndicatorTween = this._phaser.add
					.tween(bounceSrc)
					.to(bounceTgt, durationMS, Phaser.Easing.Quadratic.InOut, true)
					.yoyo(true)
					.repeat(-1);
				this._healthIndicatorTween.onUpdateCallback(() => {
					this._healthBounceOffset = bounceSrc.offset;
				});
			}
		} else {
			if (this._healthIndicatorTween && this._healthIndicatorTween.isRunning) {
				this._healthIndicatorTween.stop();
				this._healthBounceOffset = 0;
			}
		}
	}

	getPos() {
		return this._group.position;
	}

	private _enableSkipTurnInput(sprite: Phaser.Sprite) {
		sprite.inputEnabled = true;
		sprite.input.priorityID = 10;
		sprite.input.useHandCursor = true;
		sprite.events.onInputDown.add(() => {
			if (this._creature.noActionPossible) {
				// Clear flag and fade out before click so cleanup queryMove doesn't
				// re-run querySelf and spawn a second marker mid-fade.
				this._creature.noActionPossible = false;
				this.fadeOutNoActionHints();
				this._creature.game.UI.btnSkipTurn.click();
			}
		});
	}

	private isNoActionHintType(hintType: string): boolean {
		return hintType === 'no_action' || hintType === 'no_action_bg' || hintType === 'no_action_icon';
	}

	private setSkipButtonNoActionVisibility(hidden: boolean) {
		void hidden;
	}

	private restartNoActionHintBounce() {
		if (this._noActionHintElements.length > 0) {
			this._noActionHintElements = this._noActionHintElements.filter((hint) => hint.exists);
			this._noActionHintElements.forEach((hint) => {
				if (typeof hint.data?.baseY !== 'number') {
					hint.data.baseY = hint.y;
				}

				if (hint.data?.tweenBounce) {
					return;
				}

				const bounceSrc = { offset: 0 };
				const bounceTgt = { offset: -10 };
				hint.y = hint.data.baseY;
				hint.data.tweenBounce = this._phaser.add
					.tween(bounceSrc)
					.to(bounceTgt, 350, Phaser.Easing.Quadratic.InOut, true)
					.yoyo(true)
					.repeat(-1);
				hint.data.tweenBounce.onUpdateCallback(() => {
					if (!hint.exists) {
						return;
					}
					hint.y = hint.data.baseY + bounceSrc.offset;
				});
			});
			return;
		}

		if (!this._noActionHintGroup || !this._noActionHintGroup.exists) {
			return;
		}

		if (this._noActionHintTween && this._noActionHintTween.isRunning) {
			return;
		}

		const bounceHeight = 10;
		const durationMS = 350;
		const bounceSrc = { offset: 0 };
		const bounceTgt = { offset: -bounceHeight };
		this._noActionHintGroup.y = 0;

		this._noActionHintTween = this._phaser.add
			.tween(bounceSrc)
			.to(bounceTgt, durationMS, Phaser.Easing.Quadratic.InOut, true)
			.yoyo(true)
			.repeat(-1);
		this._noActionHintTween.onUpdateCallback(() => {
			if (!this._noActionHintGroup || !this._noActionHintGroup.exists) {
				return;
			}
			this._noActionHintGroup.y = bounceSrc.offset;
		});
	}

	private destroyNoActionHintGroup() {
		if (this._noActionHintTween) {
			this._noActionHintTween.stop();
			this._noActionHintTween = null;
		}

		if (this._noActionHintGroup && this._noActionHintGroup.exists) {
			this._noActionHintGroup.destroy(true);
		}
		this._noActionHintGroup = null;
	}

	hint(text: string, hintType: CreatureHintType) {
		const tooltipSpeed = 250;
		const tooltipDisplaySpeed = 500;
		const tooltipTransition = Phaser.Easing.Linear.None;
		// Keep no-action hint bounce synced with the health indicator bounce feel.
		const noActionBounceHeight = 10;
		const noActionBounceSpeed = 350;
		const startNoActionBounce = (hintElement: Phaser.Text | Phaser.Sprite) => {
			if (hintElement.data.tweenBounce && hintElement.data.tweenBounce.isRunning) {
				return;
			}

			if (typeof hintElement.data.baseY !== 'number') {
				hintElement.data.baseY = hintElement.y;
			}

			hintElement.y = hintElement.data.baseY;
			const bounceSrc = { offset: 0 };
			const bounceTgt = { offset: -noActionBounceHeight };

			hintElement.data.tweenBounce = this._phaser.add
				.tween(bounceSrc)
				.to(bounceTgt, noActionBounceSpeed, Phaser.Easing.Quadratic.InOut, true)
				.yoyo(true)
				.repeat(-1);
			hintElement.data.tweenBounce.onUpdateCallback(() => {
				hintElement.y = hintElement.data.baseY + bounceSrc.offset;
			});
		};

		const hintColor: Record<CreatureHintType, { fill: string; stroke: string }> = {
			damage: {
				fill: '#ff0000',
				stroke: '#000000',
			},
			confirm: {
				fill: '#ffffff',
				stroke: '#000000',
			},
			gamehintblack: {
				fill: '#ffffff',
				stroke: '#000000',
			},
			healing: {
				fill: '#00ff00',
				stroke: '#000000',
			},
			msg_effects: {
				fill: '#ffff00',
				stroke: '#000000',
			},
			creature_name: {
				fill: '#ffffff',
				stroke: '#AAAAAA',
			},
			no_action: {
				fill: '#ffffff',
				stroke: '#000000',
			},
		};

		const style = {
			...{
				font: 'bold 20pt Play',
				fill: '#ff0000',
				align: 'center',
				stroke: '#000000',
				strokeThickness: 2,
			},
			...(hintColor.hasOwnProperty(hintType) ? hintColor[hintType] : {}),
		};

		const isSkipTurnConfirm = hintType === 'confirm' && text === 'Skip turn';
		if (isSkipTurnConfirm) {
			const existingSkipHints: Array<Phaser.Text | Phaser.Sprite> = [];
			let hasSkipTurnLabel = false;
			this._hintGrp.forEach(
				(hint: Phaser.Text | Phaser.Sprite) => {
					if (!hint.exists || !hint.data) {
						return;
					}

					if (hint.data.hintType !== 'confirm' && !this.isNoActionHintType(hint.data.hintType)) {
						return;
					}

					existingSkipHints.push(hint);
					if (hint instanceof Phaser.Text && hint.text === 'Skip turn') {
						hasSkipTurnLabel = true;
					}
				},
				this,
				true,
			);

			if (existingSkipHints.length > 0 && hasSkipTurnLabel) {
				this._noActionHintElements = [];
				existingSkipHints.forEach((hint) => {
					if (hint.data.tweenBounce) {
						hint.data.tweenBounce.stop();
						hint.data.tweenBounce = null;
					}
					if (hint.data.tweenPos) {
						hint.data.tweenPos.stop();
						hint.data.tweenPos = null;
					}
					if (hint.data.tweenAlpha) {
						hint.data.tweenAlpha.stop();
						hint.data.tweenAlpha = null;
					}
					hint.alpha = 1;
					hint.data.hintType = 'confirm';
					hint.data.skipTurnStatic = true;
				});
				return;
			}
		}

		if (hintType === 'no_action') {
			const existingConfirmHints: Array<Phaser.Text | Phaser.Sprite> = [];
			this._hintGrp.forEach(
				(hint: Phaser.Text | Phaser.Sprite) => {
					if (!hint.exists || hint.data?.hintType !== 'confirm') {
						return;
					}

					existingConfirmHints.push(hint);
				},
				this,
				true,
			);

			if (existingConfirmHints.length > 0) {
				this.destroyNoActionHintGroup();
				this._noActionHintElements = [];

				existingConfirmHints.forEach((hint) => {
					if (hint.data?.tweenPos) {
						hint.data.tweenPos.stop();
						hint.data.tweenPos = null;
					}
					if (hint.data?.tweenAlpha) {
						hint.data.tweenAlpha.stop();
						hint.data.tweenAlpha = null;
					}
					hint.alpha = 1;

					if (hint instanceof Phaser.Text) {
						hint.data.hintType = 'no_action';
					} else if ((hint as Phaser.Sprite).key === 'skip') {
						hint.data.hintType = 'no_action_icon';
					} else {
						hint.data.hintType = 'no_action_bg';
					}

					this._noActionHintElements.push(hint);
				});

				this.restartNoActionHintBounce();
				return;
			}

			if (this._noActionHintElements.some((hint) => hint.exists)) {
				this.restartNoActionHintBounce();
				return;
			}

			this.clearHints(['confirm', 'no_action']);
			this.destroyNoActionHintGroup();

			const frame = this._phaser.add.sprite(0, 50, 'frame');
			const frameBackground = this._phaser.make.bitmapData(frame.width, frame.height);
			frameBackground.ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
			frameBackground.ctx.fillRect(0, 0, frameBackground.width, frameBackground.height);
			frameBackground.draw('frame', 0, 0);
			frame.destroy();

			const noActionFrame = this._phaser.add.sprite(0, 50, frameBackground);
			noActionFrame.anchor.setTo(0.5, 0.175);
			noActionFrame.setScaleMinMax(0.75, 0.75, 0.75, 0.75);
			noActionFrame.alpha = 0;
			noActionFrame.visible = true;
			noActionFrame.data.hintType = 'no_action_bg';
			noActionFrame.data.tweenAlpha = null;
			noActionFrame.data.tweenPos = null;
			noActionFrame.data.tweenBounce = null;
			this._phaser.add
				.tween(noActionFrame)
				.to({ alpha: 1 }, tooltipSpeed, tooltipTransition)
				.start();
			this._enableSkipTurnInput(noActionFrame);

			const noActionIcon = this._phaser.add.sprite(0, 29, 'skip');
			noActionIcon.anchor.setTo(0.5, 0.745);
			noActionIcon.setScaleMinMax(0.15, 0.15, 0.15, 0.15);
			noActionIcon.alpha = 0;
			noActionIcon.visible = true;
			noActionIcon.data.hintType = 'no_action_icon';
			noActionIcon.data.tweenAlpha = null;
			noActionIcon.data.tweenPos = null;
			noActionIcon.data.tweenBounce = null;
			this._phaser.add
				.tween(noActionIcon)
				.to({ alpha: 1 }, tooltipSpeed, tooltipTransition)
				.start();
			this._enableSkipTurnInput(noActionIcon);

			const noActionText = this._phaser.add.text(0, 50, text, style);
			noActionText.anchor.setTo(0.5, 0.5);
			noActionText.alpha = 0;
			noActionText.data.hintType = 'no_action';
			noActionText.data.tweenAlpha = null;
			noActionText.data.tweenPos = null;
			noActionText.data.tweenBounce = null;
			this._phaser.add
				.tween(noActionText)
				.to({ alpha: 1 }, tooltipSpeed, tooltipTransition)
				.start();

			this._hintGrp.add(noActionFrame);
			this._hintGrp.add(noActionIcon);
			this._hintGrp.add(noActionText);
			this._noActionHintElements = [noActionFrame, noActionIcon, noActionText];

			this._hintGrp.forEach(
				(hint: Phaser.Text | Phaser.Sprite) => {
					const index = this._hintGrp.total - this._hintGrp.getIndex(hint) - 1;
					const offset = -50 * index;

					if (hint.data.tweenBounce) {
						hint.data.tweenBounce.stop();
						hint.data.tweenBounce = null;
					}

					if (hint.data.tweenPos) {
						hint.data.tweenPos.stop();
						hint.data.tweenPos = null;
					}

					if (this.isNoActionHintType(hint.data.hintType)) {
						hint.y = offset;
						startNoActionBounce(hint);
						return;
					}

					hint.data.tweenPos = this._phaser.add
						.tween(hint)
						.to({ y: offset }, tooltipSpeed, tooltipTransition)
						.start();
				},
				this,
				true,
			);
			this.restartNoActionHintBounce();
			return;
		}

		// Remove constant element
		// Animation length reduced from 250 to 100 to prevent animation overlap
		this._hintGrp.forEach(
			(hint: Phaser.Text | Phaser.Sprite) => {
				if (hint.data.hintType === 'confirm' || this.isNoActionHintType(hint.data.hintType)) {
					if (hint.data.tweenBounce) {
						hint.data.tweenBounce.stop();
						hint.data.tweenBounce = null;
					}

					hint.data.hintType = 'confirm_deleted';
					hint.data.tweenAlpha = this._phaser.add
						.tween(hint)
						.to({ alpha: 0 }, 100, tooltipTransition)
						.start();
					hint.data.tweenAlpha.onComplete.add(() => hint.destroy());
				}
			},
			this,
			true,
		);

		const hint = this._phaser.add.text(0, 50, text, style);
		hint.anchor.setTo(0.5, 0.5);

		hint.alpha = isSkipTurnConfirm ? 1 : 0;
		hint.data.hintType = hintType;
		hint.data.tweenAlpha = null;
		hint.data.tweenPos = null;
		hint.data.tweenBounce = null;
		hint.data.skipTurnStatic = isSkipTurnConfirm;

		if (hintType === 'confirm') {
			if (!isSkipTurnConfirm) {
				hint.data.tweenAlpha = this._phaser.add
					.tween(hint)
					.to({ alpha: 1 }, tooltipSpeed, tooltipTransition)
					.start();
			}
		} else {
			hint.data.tweenAlpha = this._phaser.add
				.tween(hint)
				.to({ alpha: 1 }, tooltipSpeed, tooltipTransition)
				.to({ alpha: 1 }, tooltipDisplaySpeed, tooltipTransition)
				.to({ alpha: 0 }, tooltipSpeed, tooltipTransition)
				.start();
			hint.data.tweenAlpha.onComplete.add(() => hint.destroy());
		}

		if (hintType === 'confirm') {
			// Add "Skip turn" frame
			const frame = this._phaser.add.sprite(0, 50, 'frame');
			const frameBackground = this._phaser.make.bitmapData(frame.width, frame.height);
			frameBackground.ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
			frameBackground.ctx.fillRect(0, 0, frameBackground.width, frameBackground.height);
			frameBackground.draw('frame', 0, 0);
			// Destroy the temporary frame sprite after using it as a texture source
			// to prevent it from lingering in the upper-left corner of the canvas
			frame.destroy();
			const combinedSprite = this._phaser.add.sprite(0, 50, frameBackground);
			combinedSprite.anchor.setTo(0.5, 0.175);
			combinedSprite.setScaleMinMax(0.75, 0.75, 0.75, 0.75);
			combinedSprite.alpha = isSkipTurnConfirm ? 1 : 0;
			combinedSprite.visible = true;
			combinedSprite.data.hintType = hintType;
			combinedSprite.data.tweenAlpha = null;
			combinedSprite.data.tweenPos = null;
			combinedSprite.data.tweenBounce = null;
			combinedSprite.data.skipTurnStatic = isSkipTurnConfirm;
			if (!isSkipTurnConfirm) {
				this._phaser.add
					.tween(combinedSprite)
					.to({ alpha: 1 }, tooltipSpeed, tooltipTransition)
					.start();
			}
			this._enableSkipTurnInput(combinedSprite);
			this._hintGrp.add(combinedSprite);

			// Add "Skip turn" icon
			const skipTurnIcon = this._phaser.add.sprite(0, 29, 'skip');
			skipTurnIcon.anchor.setTo(0.5, 0.745);
			skipTurnIcon.setScaleMinMax(0.15, 0.15, 0.15, 0.15);
			skipTurnIcon.alpha = isSkipTurnConfirm ? 1 : 0;
			skipTurnIcon.visible = true;
			skipTurnIcon.data.hintType = hintType;
			skipTurnIcon.data.tweenAlpha = null;
			skipTurnIcon.data.tweenPos = null;
			skipTurnIcon.data.tweenBounce = null;
			skipTurnIcon.data.skipTurnStatic = isSkipTurnConfirm;
			if (!isSkipTurnConfirm) {
				this._phaser.add
					.tween(skipTurnIcon)
					.to({ alpha: 1 }, tooltipSpeed, tooltipTransition)
					.start();
			}
			this._enableSkipTurnInput(skipTurnIcon);
			this._hintGrp.add(skipTurnIcon);
		}

		this._hintGrp.add(hint);

		// Stacking
		this._hintGrp.forEach(
			(hint: Phaser.Text | Phaser.Sprite) => {
				const index = this._hintGrp.total - this._hintGrp.getIndex(hint) - 1;
				const offset = -50 * index;

				if (hint.data.tweenBounce) {
					hint.data.tweenBounce.stop();
					hint.data.tweenBounce = null;
				}

				if (hint.data.tweenPos) {
					hint.data.tweenPos.stop();
					hint.data.tweenPos = null;
				}

				if (hint.data.hintType === 'no_action') {
					this.setSkipButtonNoActionVisibility(true);
					// Keep no-action hints stable on first show: place immediately, then bounce.
					hint.y = offset;
					startNoActionBounce(hint);
					return;
				}

				if (hint.data.skipTurnStatic) {
					hint.y = offset;
					return;
				}

				hint.data.tweenPos = this._phaser.add
					.tween(hint)
					.to({ y: offset }, tooltipSpeed, tooltipTransition)
					.start();
			},
			this,
			true,
		);
	}

	stopNoActionHintBounce() {
		this._noActionHintElements.forEach((hint) => {
			if (!hint.exists || !hint.data) {
				return;
			}

			if (hint.data.tweenBounce) {
				hint.data.tweenBounce.stop();
				hint.data.tweenBounce = null;
			}

			if (typeof hint.data.baseY === 'number') {
				hint.y = hint.data.baseY;
			}
		});
		this._noActionHintElements = this._noActionHintElements.filter((hint) => hint.exists);

		if (this._noActionHintTween) {
			this._noActionHintTween.stop();
			this._noActionHintTween = null;
		}
		if (this._noActionHintGroup && this._noActionHintGroup.exists) {
			this._noActionHintGroup.y = 0;
		}
	}

	fadeOutNoActionHints() {
		const tooltipSpeed = 250;
		const tooltipTransition = Phaser.Easing.Linear.None;
		const noActionHints = this._noActionHintElements.filter((hint) => hint.exists);
		this._noActionHintElements = [];

		// Tag as deleted immediately so any concurrent hint('Skip turn','confirm') call
		// (e.g. from cleanup queryMove on turn-end) does not detect these as live hints
		// and spawn a second marker on top of the fading one.
		noActionHints.forEach((hint) => {
			if (hint.data) {
				hint.data.hintType = 'confirm_deleted';
			}
		});

		noActionHints.forEach((hint) => {
			if (!hint.data) {
				return;
			}

			if (hint.data.tweenBounce) {
				hint.data.tweenBounce.stop();
				hint.data.tweenBounce = null;
			}
			if (hint.data.tweenPos) {
				hint.data.tweenPos.stop();
				hint.data.tweenPos = null;
			}
			if (hint.data.tweenAlpha) {
				hint.data.tweenAlpha.stop();
				hint.data.tweenAlpha = null;
			}

			const targetY = hint.y - 30;
			hint.data.tweenPos = this._phaser.add
				.tween(hint)
				.to({ y: targetY }, tooltipSpeed, tooltipTransition)
				.start();

			hint.data.tweenAlpha = this._phaser.add
				.tween(hint)
				.to({ alpha: 0 }, tooltipSpeed, tooltipTransition)
				.start();
			hint.data.tweenAlpha.onComplete.add(() => {
				if (hint.exists) {
					hint.destroy();
				}
			});
		});

		if (this._noActionHintTween) {
			this._noActionHintTween.stop();
			this._noActionHintTween = null;
		}
		if (this._noActionHintGroup && this._noActionHintGroup.exists) {
			const group = this._noActionHintGroup;
			this._noActionHintGroup = null;

			this._phaser.add
				.tween(group)
				.to({ y: group.y - 30 }, tooltipSpeed, tooltipTransition)
				.start();
			const fadeTween = this._phaser.add
				.tween(group)
				.to({ alpha: 0 }, tooltipSpeed, tooltipTransition)
				.start();
			fadeTween.onComplete.add(() => group.destroy(true));
		}
	}

	clearHints(hintTypes: CreatureHintType[] = ['confirm', 'no_action']) {
		const tooltipTransition = Phaser.Easing.Linear.None;
		if (hintTypes.includes('no_action')) {
			this._noActionHintElements = [];
			this.destroyNoActionHintGroup();
		}

		this._hintGrp.forEach(
			(hint: Phaser.Text | Phaser.Sprite) => {
				if (!hint.data || typeof hint.data.hintType !== 'string') {
					return;
				}

				const isNoAction = this.isNoActionHintType(hint.data.hintType);
				if (
					!hintTypes.includes(hint.data.hintType) &&
					!(isNoAction && hintTypes.includes('no_action'))
				) {
					return;
				}

				hint.data.hintType = 'confirm_deleted';
				if (hint.data.tweenBounce) {
					hint.data.tweenBounce.stop();
					hint.data.tweenBounce = null;
				}
				if (hint.data.tweenAlpha) {
					hint.data.tweenAlpha.stop();
				}
				hint.data.tweenAlpha = this._phaser.add
					.tween(hint)
					.to({ alpha: 0 }, 100, tooltipTransition)
					.start();
				hint.data.tweenAlpha.onComplete.add(() => hint.destroy());
			},
			this,
			true,
		);
	}

	destroy() {
		this._creature.game.animations.disposeInfernalCardboardEffect(this._creature);
		this._group.parent?.removeChild(this._group);
	}
}

export type CreatureHintType =
	| 'confirm'
	| 'damage'
	| 'gamehintblack'
	| 'healing'
	| 'msg_effects'
	| 'creature_name'
	| 'no_action';

type HealthBubbleType = 'plasma' | 'frozen' | 'health';
